import React from 'react';
import moment from 'moment';
import { DateTime, Duration } from 'luxon';
import { TaskHelpers, FormHelpers } from 'ontraccr-common';
import { Popover } from 'antd';

import { isNullOrUndefined } from '../../../../helpers/helpers';
import Permissions from '../../../../auth/Permissions';

import {
  UNPHASED,
  getAllTimesText,
  getTimeText,
  getClassOptions,
  getCostcodeOptions,
  getLocalOptions,
  getPhaseOptions,
  getProjectOptions,
} from '../../../../clock/ManualEntry/manualEntryHelpers';
import { sortByObjProperty, collator } from '../../../../common/helpers';
import {
  compareProjectLabel,
  formatProjectLabelFromCompanySettings,
} from '../../../../projects/projectHelpers';
import { formatEquipmentLabelFromCompanySettings } from '../../../../equipment/equipment.helpers';
import { currencyFormatter } from '../../../../helpers/inputParsers';
import { getPhaseCostcodeTreeData, getAllowedDivisionIds } from '../../../formHelpers';
import { TIME_TYPE_FLAG } from '../../../../constants/Flags';
import {
  DEFAULT_FIELD_TYPES,
  LABOUR_PROJECT_BILLING_FIELD_TYPES,
  LUXON_DATE_FORMAT,
  LUXON_TIME_FORMAT,
  LUXON_TIME_FORMAT_24H,
} from './TimeEntryTable.constants';

export default {};

export const getTimeEntryFieldTypesOrdered = (hasTimeTypesFlag) => {
  if (!hasTimeTypesFlag) return DEFAULT_FIELD_TYPES;
  const i = DEFAULT_FIELD_TYPES.indexOf('hourlyBillingRate');
  return [
    ...DEFAULT_FIELD_TYPES.slice(0, i + 1),
    ...LABOUR_PROJECT_BILLING_FIELD_TYPES,
    ...DEFAULT_FIELD_TYPES.slice(i + 1),
  ];
};

export const parseDateTimeFromString = (date) => {
  const formats = ['YYYY-MM-DD', 'M/D/YYYY', 'M/DD/YYYY', 'M/DD/YY'];

  let parsedDate = moment.invalid();

  formats.forEach((format) => {
    const momentDate = moment(date, format, true);
    if (momentDate.isValid()) {
      parsedDate = momentDate;
    }
  });

  return parsedDate;
};

export const momentToLuxon = (date) => {
  const dateMillis = date.valueOf();
  return DateTime.fromMillis(dateMillis);
};

export const luxonToMoment = (date) => {
  const dateMillis = date.toMillis();
  return moment(dateMillis);
};

// Defaults
export const simpleEquality = (a, b) => a === b;
export const defaultFormatter = ({ task, property }) => task?.[property] ?? '';
export const customFormatter = (property) => ({ task }) => defaultFormatter({ task, property });

// Is Equal Checkers
export const areDatesEqual = (a, b) => {
  if (!a) {
    return a === b;
  }

  if (isNullOrUndefined(b) || isNullOrUndefined(a)) {
    return false;
  }

  return a.valueOf() === b.valueOf();
};

const areDateTimeEqual = (a, b) => {
  if (!a) {
    return a === b;
  }

  const {
    date: aDate = 0,
    time: aTime = 0,
  } = a;

  const {
    date: bDate = 0,
    time: bTime = 0,
  } = b;

  return (aDate === bDate) && (aTime === bTime);
};

export const areStartTimesEqual = (a, b) => {
  if (!a || !b) {
    return a === b;
  }

  const times = [
    'startTime',
    'otStartTime',
    'doubleOTStartTime',
    'breakStartTime',
    'ptoStartTime',
  ];

  return times.every((key) => a[key] === b[key]);
};

export const areEndTimesEqual = (a, b) => {
  if (!a || !b) {
    return a === b;
  }

  const times = [
    'endTime',
    'otEndTime',
    'doubleOTEndTime',
    'breakEndTime',
    'ptoEndTime',
  ];

  return times.every((key) => a[key] === b[key]);
};

const areTimesEqual = (a, b) => {
  if (!a || !b) {
    return a === b;
  }

  const times = [
    'startTime',
    'endTime',
    'otStartTime',
    'otEndTime',
    'doubleOTStartTime',
    'doubleOTEndTime',
    'breakStartTime',
    'breakEndTime',
    'ptoStartTime',
    'ptoEndTime',
  ];

  return times.every((key) => a[key] === b[key]);
};

// Display Formatters
const dateFormatter = ({ task, timezone }) => {
  const { dateTimestamp, date } = task;

  if (date) return date;

  if (!dateTimestamp) {
    return null;
  }

  const dateObject = DateTime.fromMillis(dateTimestamp, { zone: timezone });
  return dateObject.toSQLDate();
};

export const isEntryTimeValid = (time, warningThreshold) => {
  const runtimes = TaskHelpers.getRuntimes(time);
  const runtime = runtimes.regularTime + runtimes.breakTime + runtimes.overtime + runtimes.doubleOT;
  // Warning threshold is in hours
  const warningThresholdInMillis = warningThreshold * TaskHelpers.MILLISECONDS_IN_SECOND * 60 * 60;
  return runtime <= warningThresholdInMillis;
};

const FormattedTimeText = ({
  enableWarningThreshold,
  warningThreshold,
  task,
  timeText,
}) => {
  if (isNullOrUndefined(timeText)) {
    return '0h';
  }

  if (!enableWarningThreshold || isEntryTimeValid(task, warningThreshold)) {
    return timeText;
  }

  return (
    <Popover
      content={`This entry exceeds the warning threshold of ${warningThreshold} hours.`}
      zIndex={1100}
      placement="left"
    >
      <div style={{ color: 'red' }}>{timeText}</div>
    </Popover>
  );
};

const timeFormatter = ({
  enableWarningThreshold,
  warningThreshold,
}) => function innerTimeFormatter({
  value: time,
  task,
  timezone,
}) {
  const timeText = getAllTimesText(
    {
      ...time,
      ...task,
      timezone,
      hourBased: true,
    },
    { showRegularTypeText: false, addTypeAsSuffix: true, returnComponent: true },
  );

  return (
    <FormattedTimeText
      enableWarningThreshold={enableWarningThreshold}
      warningThreshold={warningThreshold}
      task={task}
      timeText={timeText === '' ? '0h' : timeText}
    />
  );
};

const hourSegmentTimeFormatter = ({
  enableWarningThreshold,
  warningThreshold,
  type,
}) => function innerHourSegmentFormatter({
  task,
  timezone,
}) {
  const {
    startTime,
    endTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    originalStart,
    originalEnd,
  } = task;

  let segmentStart = null;
  let segmentEnd = null;
  switch (type) {
    case 'regularHours':
      segmentStart = startTime;
      segmentEnd = endTime;
      break;
    case 'otHours':
      segmentStart = otStartTime;
      segmentEnd = otEndTime;
      break;
    case 'doubleOTHours':
      segmentStart = doubleOTStartTime;
      segmentEnd = doubleOTEndTime;
      break;
    default:
      break;
  }

  const timeText = getTimeText({
    hourBased: true,
    type: null,
    startTime: segmentStart,
    endTime: segmentEnd,
    originalStart,
    originalEnd,
    timezone,
  }, { showZeroTimes: true });

  return (
    <FormattedTimeText
      enableWarningThreshold={enableWarningThreshold}
      warningThreshold={warningThreshold}
      task={task}
      timeText={timeText}
    />
  );
};

const dateTimeFormatter = ({ use24HourTime = false } = {}) => ({ value }) => {
  if (!value || !Object.keys(value).length) {
    return '';
  }

  const {
    date = null,
    time = null,
    timezone = DateTime.local().zoneName,
  } = value;

  if (!date && !time) {
    return '';
  }

  const luxonDate = date ? DateTime.fromMillis(date, { zone: timezone }) : null;
  const luxonTime = time ? DateTime.fromMillis(time, { zone: timezone }) : null;

  const dateString = luxonDate ? luxonDate.toFormat(LUXON_DATE_FORMAT) : '';
  const timeFormat = use24HourTime ? LUXON_TIME_FORMAT_24H : LUXON_TIME_FORMAT;
  const timeString = luxonTime ? luxonTime.toFormat(timeFormat) : '';

  return `${dateString} ${timeString}`;
};

const projectFormatter = ({
  task, projectIdMap = {}, settings, id = null,
}) => {
  const { projectId } = task;
  const trueId = id ?? projectId;
  const {
    [trueId]: {
      name,
      number,
    } = {},
  } = projectIdMap;

  return formatProjectLabelFromCompanySettings({ name, number, settings });
};

const equipmentFormatter = ({
  equipmentIdMap = {}, settings, id = null,
}) => {
  const {
    [id]: {
      name,
      code,
    } = {},
  } = equipmentIdMap;

  return formatEquipmentLabelFromCompanySettings({ name, code, settings });
};

const dropdownFormatter = ({
  task, projectIdMap, equipmentIdMap, settings, property,
}) => {
  const {
    customData: {
      [property]: {
        values = [],
        dataType,
        configProps: {
          dataType: innerDataType,
        } = {},
      } = {},
    } = {},
  } = task;

  const trueDataType = innerDataType ?? dataType;

  if (trueDataType !== 'Projects' && trueDataType !== 'Equipment') {
    return defaultFormatter({ task, property });
  }

  const formatter = trueDataType === 'Projects' ? projectFormatter : equipmentFormatter;

  return values.map(({ id }) => formatter({
    task, projectIdMap, equipmentIdMap, settings, id,
  })).join(', ');
};

const costCodeFormatter = ({ task }) => {
  const { costcode_name: name, costcode_code: code } = task;
  let costcodeValue = '';

  if (code && name) {
    costcodeValue = `${code} - ${name}`;
  }

  return costcodeValue;
};

const phaseFormatter = ({ task, value }) => {
  const { phaseId } = task;
  if (phaseId === UNPHASED) return UNPHASED;
  return defaultFormatter({ task, property: 'phase', value });
};

const currencyValueFormatter = ({ task, property }) => {
  const {
    [property]: value = 0,
  } = task;
  return currencyFormatter(value);
};

const blendedWageFormatter = ({ task, settings = {} }) => {
  const blendedWage = task.blendedUserWage ?? FormHelpers.calculateBlendedWage(task, settings);
  return currencyFormatter(blendedWage);
};

const startEndTimeFormatter = (type, settings = {}) => ({ task }) => {
  const { hourBased } = task;
  if (hourBased) return null;
  let nullObj = {};
  if (type === 'formattedStartTime') {
    nullObj = {
      endTime: null,
      originalEnd: null,
      breakEndTime: null,
      otEndTime: null,
      doubleOTEndTime: null,
      ptoEndTime: null,
    };
  } else {
    nullObj = {
      startTime: null,
      originalStart: null,
      breakStartTime: null,
      otStartTime: null,
      doubleOTStartTime: null,
      ptoStartTime: null,
    };
  }
  return getAllTimesText({
    ...task,
    ...nullObj,
  }, {
    returnComponent: true,
    showRegularTypeText: false,
    addTypeAsSuffix: true,
    showOriginalTimes: settings?.showOriginalTimes,
  });
};

// Select Source Filters
export const getSimpleSelectOptions = (key) => () => {
  switch (key) {
    case 'type': {
      return [
        {
          value: 'work',
          label: 'Work',
        }, {
          value: 'break',
          label: 'Break',
        },
      ];
    }
    case 'yes-no': {
      return [
        {
          value: 'yes',
          label: 'Yes',
        }, {
          value: 'no',
          label: 'No',
        }, {
          value: 'n/a',
          label: 'N/A',
        },
      ];
    }
    default: {
      return [];
    }
  }
};

export const getHourBasedOptions = ({ settings = {} }) => {
  const { enableHourBasedTracking = false } = settings;
  if (enableHourBasedTracking) {
    return [
      {
        value: true,
        label: 'True',
      }, {
        value: false,
        label: 'False',
      },
    ];
  }

  return [
    {
      value: false,
      label: 'False',
    },
  ];
};

export const getCostcodeCodeMap = ({
  costcodes,
  projectId,
}) => {
  const map = {};
  if (!projectId) return map;

  costcodes.forEach((cc) => {
    const { code, projectId: costcodeProjectId } = cc;
    if (costcodeProjectId !== projectId) return;

    map[code] = cc;
  });

  return map;
};

const getCostcodeSelectOptions = (props) => {
  const {
    settings = {},
    activePhases,
    filteredActiveCostcodes,
    projectId,
    phaseId,
    phase,
    divisionId,
  } = props;
  const { enableGlobalCCOnProject } = settings;

  const divisionFilteredCostcodes = divisionId
    ? filteredActiveCostcodes.filter((cc) => cc.divisionId === divisionId)
    : filteredActiveCostcodes;

  const costcodeCodeMap = getCostcodeCodeMap({
    costcodes: divisionFilteredCostcodes,
    projectId: props.projectId,
  });

  if (!enableGlobalCCOnProject || !projectId) {
    return getCostcodeOptions({ ...props, activeCostcodes: divisionFilteredCostcodes });
  }

  const isUnphased = phase === UNPHASED;

  if (!phaseId && !isUnphased) return [];

  return getPhaseCostcodeTreeData({
    ...props,
    phases: activePhases,
    costcodes: divisionFilteredCostcodes,
    includeGlobal: true,
    costcodeCodeMap,
    phaseId: isUnphased ? UNPHASED : props.phaseId,
    isClockIn: true,
  });
};

const getPhaseSelectOptions = (props) => {
  const {
    settings: {
      enableGlobalCCOnProject,
    } = {},
    projectId,
  } = props;

  const opts = getPhaseOptions(props);

  if (enableGlobalCCOnProject && projectId && !opts.find((opt) => opt.value === UNPHASED)) {
    opts.unshift({
      value: UNPHASED,
      label: UNPHASED,
    });
  }

  return opts;
};

// Helpers
export const getTableInputParameters = ({
  value,
  entry: {
    customData = {},
    ...task
  } = {},
  type,
  id,
  enableWarningThreshold,
  warningThreshold,
  settings = {},
  paidFlags = [],
  configProps = {},
}) => {
  switch (type) {
    case 'attribute': {
      const { [id]: { allowEdits = true } = {} } = customData;
      return { readOnly: !allowEdits };
    }
    case 'dateTime': {
      const {
        [id]: {
          date,
          time,
          timezone,
        } = {},
      } = customData;

      const { use24HourTime = false } = configProps;

      return {
        displayFormatter: dateTimeFormatter({ use24HourTime }),
        areEqual: areDateTimeEqual,
        value: {
          date,
          time,
          timezone,
        },
        inputType: 'dateTime',
      };
    }
    case 'date': {
      return {
        displayFormatter: dateFormatter,
        value: parseDateTimeFromString(value),
        areEqual: areDatesEqual,
        inputType: 'date',
      };
    }
    case 'time': {
      const {
        startTime = null,
        endTime = null,
        otStartTime = null,
        otEndTime = null,
        doubleOTStartTime = null,
        doubleOTEndTime = null,
        breakStartTime = null,
        breakEndTime = null,
        ptoStartTime = null,
        ptoEndTime = null,
        originalStart = null,
        originalEnd = null,
      } = task;
      return {
        displayFormatter: timeFormatter({ enableWarningThreshold, warningThreshold }),
        inputType: 'time',
        areEqual: areTimesEqual,
        property: [
          'startTime', 'endTime',
          'otStartTime', 'otEndTime',
          'doubleOTStartTime', 'doubleOTEndTime',
          'breakStartTime', 'breakEndTime',
          'ptoStartTime', 'ptoEndTime',
        ],
        value: {
          startTime,
          endTime,
          otStartTime,
          otEndTime,
          doubleOTStartTime,
          doubleOTEndTime,
          breakStartTime,
          breakEndTime,
          ptoStartTime,
          ptoEndTime,
          originalStart,
          originalEnd,
        },
      };
    }
    case 'regularHours':
    case 'otHours':
    case 'doubleOTHours': {
      const runtimeKeyMap = {
        regularHours: 'regularTime',
        otHours: 'overtime',
        doubleOTHours: 'doubleOT',
      };
      const runtimes = TaskHelpers.getRuntimes(task);
      const ms = runtimes[runtimeKeyMap[type]] || 0;
      const hours = ms / (1000 * 60 * 60);

      return {
        displayFormatter: hourSegmentTimeFormatter({
          enableWarningThreshold,
          warningThreshold,
          type,
        }),
        inputType: 'hours',
        value: hours,
        property: type,
        areEqual: simpleEquality,
        readOnly: !task.hourBased,
      };
    }
    case 'local': {
      const {
        enableLocalSelection,
      } = settings;
      const {
        localId,
      } = task;
      return {
        displayFormatter: customFormatter('local'),
        getSelectOptions: getLocalOptions,
        inputType: 'select',
        property: 'localId',
        value: localId ?? value,
        readOnly: !enableLocalSelection,
        allowClear: false,
      };
    }
    case 'class': {
      const {
        enableClassSelection,
      } = settings;
      const {
        classId,
      } = task;
      return {
        displayFormatter: customFormatter('class'),
        getSelectOptions: getClassOptions,
        inputType: 'select',
        property: 'classId',
        value: classId ?? value,
        readOnly: !enableClassSelection,
        allowClear: false,
      };
    }
    case 'division': {
      const { divisionId: entryDivisionId } = task;
      return {
        displayFormatter: customFormatter('division'),
        getSelectOptions: ({
          divisions: templateDivisions = [],
          divisionMap = {},
          userId,
          userDivisions = {},
        } = {}) => {
          const relevantDivisionIds = getAllowedDivisionIds({
            userDivisions,
            userId,
            formDivisionIds: templateDivisions,
          });
          return relevantDivisionIds.map((divId) => ({
            value: divId,
            label: divisionMap[divId]?.name ?? divId,
          }));
        },
        inputType: 'select',
        property: 'divisionId',
        value: entryDivisionId ?? value,
      };
    }
    case 'project': {
      const {
        projectId: projectValue,
      } = task;
      return {
        displayFormatter: projectFormatter,
        getSelectOptions: getProjectOptions,
        inputType: 'select',
        property: 'projectId',
        value: projectValue ?? value,
      };
    }
    case 'costcode': {
      return {
        displayFormatter: costCodeFormatter,
        getSelectOptions: getCostcodeSelectOptions,
        inputType: 'select',
        property: 'costcodeId',
      };
    }
    case 'phase': {
      return {
        displayFormatter: phaseFormatter,
        getSelectOptions: getPhaseSelectOptions,
        inputType: 'select',
        property: 'phaseId',
      };
    }
    case 'dropdown': {
      return {
        inputType: 'select',
        displayFormatter: dropdownFormatter,
      };
    }
    case 'yes-no':
      return {
        inputType: 'select',
      };
    case 'type': {
      return {
        inputType: 'select',
        readOnly: paidFlags.includes(TIME_TYPE_FLAG),
      };
    }
    case 'hourBased': {
      return {
        displayFormatter: ({ value: val }) => (val ? 'True' : 'False'),
        getSelectOptions: getHourBasedOptions,
        inputType: 'select',
      };
    }
    case 'userWage':
    case 'hourlyBillingRate':
    case 'regularBillingRate':
    case 'otBillingRate':
    case 'dtBillingRate':
    case 'hourlyCost':
    case 'dailyCost': {
      let inputValue = value;
      if (type === 'userWage' && settings?.enableManualOT) {
        inputValue = task.blendedUserWage ?? FormHelpers.calculateBlendedWage(task, settings);
      }

      return {
        displayFormatter: type === 'userWage' ? blendedWageFormatter : currencyValueFormatter,
        allowClear: false,
        inputType: 'currency',
        value: inputValue,
      };
    }
    case 'formattedStartTime':
    case 'formattedEndTime': {
      const {
        startTime = null,
        endTime = null,
        otStartTime = null,
        otEndTime = null,
        doubleOTStartTime = null,
        doubleOTEndTime = null,
        breakStartTime = null,
        breakEndTime = null,
        ptoStartTime = null,
        ptoEndTime = null,
        originalStart = null,
        originalEnd = null,
      } = task;
      return {
        displayFormatter: startEndTimeFormatter(type, settings),
        inputType: 'startEndTime',
        areEqual: areTimesEqual,
        property: [
          'startTime', 'endTime',
          'otStartTime', 'otEndTime',
          'doubleOTStartTime', 'doubleOTEndTime',
          'breakStartTime', 'breakEndTime',
          'ptoStartTime', 'ptoEndTime',
          'originalStart', 'originalEnd',
        ],
        value: {
          startTime,
          endTime,
          otStartTime,
          otEndTime,
          doubleOTStartTime,
          doubleOTEndTime,
          breakStartTime,
          breakEndTime,
          ptoStartTime,
          ptoEndTime,
          originalStart,
          originalEnd,
        },
      };
    }
    case 'excludeFromAutoBreak': {
      return {
        displayFormatter: (res) => (res?.value ? 'Yes' : 'No'),
        inputType: 'switch',
        value: !!task.excludeFromAutoBreak,
        inputStyle: {
          width: 44,
          borderRadius: undefined,
          marginTop: 5,
        },
      };
    }
    case 'isNightShift': {
      return {
        displayFormatter: (res) => (res?.value ? 'Yes' : 'No'),
        inputType: 'switch',
        value: !!task.isNightShift,
        inputStyle: {
          width: 44,
          borderRadius: undefined,
          marginTop: 5,
        },
      };
    }
    default: {
      return {
        displayFormatter: defaultFormatter,
        areEqual: simpleEquality,
        getSelectOptions: () => [],
        value,
        inputType: 'text',
        property: type,
      };
    }
  }
};

export const getColumnWidth = (type) => {
  switch (type) {
    case 'date': return 105;
    case 'type': return 80;
    case 'costcode': return 200;
    case 'phase':
    case 'project': return 150;
    case 'time': return 200;
    case 'enteredVia': return 200;
    case 'formattedStartTime':
    case 'formattedEndTime': return 200;
    default: return 100;
  }
};

/*
  This is a rough calculation of the width of the radio or checkbox dropdown
  7.5px per character, 40/46x per options (including button size and padding)
*/
export const getRadioOrCheckboxWidth = (options, isRadio) => {
  const length = options?.length;

  if (!length) return 260;

  const mod = isRadio ? 40 : 46;

  const width = (
    options.reduce((acc, option) => acc + ((option.name.length ?? 0) * 7.5), 0) + (length * mod)
  );

  return Math.max(width, 260);
};

/**
 * Converts both Pascal & Kebab-Case text to camelCase
 *
 * Pascal Example: 'SubContracts' would return 'subContracts'
 *
 * Kebab Example: 'Sub-Contracts' would return 'subContracts'
 * @param {string} str
 * @returns {str}
 */
export const convertPascalCaseToCamelCase = (str) => {
  // Remove hyphens
  const parsedStr = str.replace(/-/g, '');

  // Split the string based on capital letters
  const words = parsedStr.match(/[A-Z][a-z]*/g);

  // Convert the first word to lowercase
  words[0] = words[0].toLowerCase();

  // Combine the words back, with the first letter of subsequent words capitalized
  const camelCase = words.join('');

  return camelCase;
};

const getRelevantDropdownValueId = (type, { multiple = false } = {}) => {
  let camelCaseName = convertPascalCaseToCamelCase(type);

  if (camelCaseName.endsWith('s')) {
    camelCaseName = camelCaseName.slice(0, -1);
  }

  if (multiple) return `${camelCaseName}Ids`;

  return `${camelCaseName}Id`;
};

export const getRelevantDropdownValues = (customData, { multiple = false } = {}) => {
  if (!customData) {
    return {};
  }
  const valueMap = {};
  const dropdownFields = Object.values(customData)
    .filter(({ fieldId = '', type, values }) => (
      fieldId.startsWith('field-')
      && type === 'dropdown')
      && (values && Array.isArray(values)));

  dropdownFields.forEach(({ dataType = '', values = [] }) => {
    if (dataType && values) {
      const id = getRelevantDropdownValueId(dataType, { multiple });

      if (multiple) {
        const value = values.map(({ id: val }) => val);
        valueMap[id] = value;
      } else {
        const [{ id: value = null } = {}] = values;
        valueMap[id] = value;
      }
    }
  });

  return valueMap;
};

export const parseCustomDataFromTemplate = (response = {}, template = {}) => {
  const customData = {};

  Object.entries(template).forEach(([id, field]) => {
    const ourResponse = response[id] ?? {};
    if (field.type === 'yes-no') {
      ourResponse.value = TaskHelpers.parseYesNoResponse(ourResponse, false);
    }
    customData[id] = {
      ...field,
      ...ourResponse,
    };
  });

  return customData;
};

const handleHourBasedEntry = ({ entry, formattedEntry }) => {
  const {
    dateTimestamp: entryDate,
    hours = 0,
    minutes = 0,
    otHours,
    otMinutes,
    breakHours,
    breakMinutes,
    doubleOTHours,
    doubleOTMinutes,
    ptoHours,
    ptoMinutes,
    startTime,
    endTime,
    otStartTime,
    otEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    breakStartTime,
    breakEndTime,
    ptoStartTime,
    ptoEndTime,
  } = entry;

  const formattedEntryRef = formattedEntry;

  const safeStartTime = entryDate ? DateTime.fromMillis(entryDate).startOf('day').toMillis() : null;

  const durationMs = Duration.fromObject({ hours, minutes }).as('milliseconds');
  formattedEntryRef.startTime = startTime ?? safeStartTime;
  formattedEntryRef.endTime = endTime ?? formattedEntry.startTime + durationMs;

  if (otHours || otMinutes) {
    const otDurationMs = Duration.fromObject({ hours: otHours, minutes: otMinutes }).as('milliseconds');
    formattedEntryRef.otStartTime = otStartTime ?? formattedEntry.endTime;
    formattedEntryRef.otEndTime = otEndTime ?? formattedEntry.otStartTime + otDurationMs;
  }

  if (breakHours || breakMinutes) {
    const breakDurationMs = Duration.fromObject({ hours: breakHours, minutes: breakMinutes }).as('milliseconds');
    formattedEntryRef.breakStartTime = breakStartTime ?? formattedEntry.endTime;
    formattedEntryRef.breakEndTime = breakEndTime
    ?? formattedEntry.breakStartTime + breakDurationMs;
  }

  if (doubleOTHours || doubleOTMinutes) {
    const doubleOTDurationMs = Duration.fromObject({ hours: doubleOTHours, minutes: doubleOTMinutes }).as('milliseconds');
    formattedEntryRef.doubleOTStartTime = doubleOTStartTime ?? formattedEntry.endTime;
    formattedEntryRef.doubleOTEndTime = doubleOTEndTime
    ?? formattedEntry.doubleOTStartTime + doubleOTDurationMs;
  }

  if (ptoHours || ptoMinutes) {
    const ptoDurationMs = Duration.fromObject({ hours: ptoHours, minutes: ptoMinutes }).as('milliseconds');
    formattedEntryRef.ptoStartTime = ptoStartTime ?? formattedEntry.endTime;
    formattedEntryRef.ptoEndTime = ptoEndTime ?? formattedEntry.ptoStartTime + ptoDurationMs;
  }
};

const handleNonHourBasedEntry = ({ entry, formattedEntry }) => {
  const formattedEntryRef = formattedEntry;

  const {
    startTime,
    endTime,
    otStartTime,
    otEndTime,
    breakStartTime,
    breakEndTime,
    doubleOTStartTime,
    doubleOTEndTime,
    ptoStartTime,
    ptoEndTime,
    originalStart,
    originalEnd,
  } = entry;
  formattedEntryRef.startTime = startTime;
  formattedEntryRef.endTime = endTime;
  formattedEntryRef.otStartTime = otStartTime;
  formattedEntryRef.otEndTime = otEndTime;
  formattedEntryRef.breakStartTime = breakStartTime;
  formattedEntryRef.breakEndTime = breakEndTime;
  formattedEntryRef.doubleOTStartTime = doubleOTStartTime;
  formattedEntryRef.doubleOTEndTime = doubleOTEndTime;
  formattedEntryRef.ptoStartTime = ptoStartTime;
  formattedEntryRef.ptoEndTime = ptoEndTime;
  formattedEntryRef.originalStart = originalStart;
  formattedEntryRef.originalEnd = originalEnd;
};

export const formatSelectedEntry = ({
  selectedEntry,
  userId,
  timezone,
}) => {
  if (!selectedEntry) return;
  const {
    id: entryId,
    userId: entryUserId,
    divisionId: entryDivisionId,
    type,
    dateTimestamp: entryDate,
    hourBased,
    projectId: entryProjectId,
    phaseId: entryPhaseId,
    costcodeId: entryCostcodeId,
    localId: entryLocalId,
    classId: entryClassId,
    sageShiftId: entrySageShiftId,
    note,
    customData,
    customDataArray,
    phasedCostcodeId,
    isNightShift,
    excludeFromAutoBreak,
    isPreloaded,
  } = selectedEntry;

  const formattedEntry = {
    id: entryId,
    userId: entryUserId || userId || Permissions.id,
    divisionId: entryDivisionId,
    type,
    date: entryDate
      ? DateTime.fromMillis(entryDate, { zone: timezone }).toISODate()
      : undefined,
    hourBased,
    projectId: entryProjectId,
    phaseId: entryPhaseId || (entryProjectId && entryCostcodeId ? 'Unphased' : undefined),
    costcodeId: phasedCostcodeId ?? entryCostcodeId,
    localId: entryLocalId,
    classId: entryClassId,
    sageShiftId: entrySageShiftId,
    note,
    customData: customDataArray ?? customData, // array is used when editing a pre-loaded entry
    timezone,
    isNightShift: !!isNightShift,
    excludeFromAutoBreak: !!excludeFromAutoBreak,
    isPreloaded: !!isPreloaded,
  };

  if (hourBased) {
    handleHourBasedEntry({ entry: selectedEntry, formattedEntry });
  } else {
    handleNonHourBasedEntry({ entry: selectedEntry, formattedEntry });
  }
  return formattedEntry; // eslint-disable-line consistent-return
};

export const DROPDOWN_CHANGE_RELATIONS = {
  Projects: new Set([
    'Customers',
    'Costcodes',
    'Equipment',
    'Sub-Contracts',
    'CompletedForms',
    'Buckets',
    'PayableSubContracts',
  ]),
  Customers: new Set([
    'Projects',
    'Contacts',
    'Buckets',
  ]),
  Vendors: new Set([
    'Contacts',
  ]),
  Equipment: new Set([
    'Buckets',
  ]),
  Buckets: new Set([
    'Customers',
    'Projects',
  ]),
};

/**
 * Calculates the day offset between two times (for overnight entries)
 * @param {number} baseTime - The base time in milliseconds
 * @param {number} otherTime - The other time in milliseconds
 * @returns {number} The day offset
 */
export const getDayOffset = (baseTime, otherTime) => {
  if (!baseTime || !otherTime) return 0;
  const baseDay = DateTime.fromMillis(baseTime).startOf('day');
  const otherDay = DateTime.fromMillis(otherTime).startOf('day');
  return Math.round(otherDay.diff(baseDay, 'days').days);
};

const SORT = {
  EQ: 0,
  LT: -1,
  GT: 1,
};

/**
 * Sorts user groups alphabetically while keeping each group's summary (header) row first.
 * @param {'ascend' | 'descend' | null } sortOrder - Active sort on the user column; used so the header row always stays first.
 */
export const compareGroupByUserColumn = (a, b, sortOrder) => {
  const primary = collator.compare(
    a.userGroupName ?? a.user ?? '',
    b.userGroupName ?? b.user ?? '',
  );
  if (primary !== SORT.EQ) return primary;

  const secondary = (a.isUserHeader ? 0 : 1) - (b.isUserHeader ? 0 : 1);
  if (sortOrder === 'descend') return -secondary;
  return secondary;
};

/**
 * Adds sorting functionality to a time entry table column
 * @param {Object} column - The column to add sorting to
 * @param {Object} projectIdMap - The project ID map
 * @param {Object} settings - The settings
 * @param {boolean} [groupByUser] - When true, user column uses group-aware sorting and controlled order
 * @param {'ascend' | 'descend' | null} [groupByUserSortOrder] - Controlled sort order for group-by-user user column
 * @returns {Object} The column with sorting functionality
 */
export const addColumnSorting = (column, {
  projectIdMap,
  settings,
  groupByUser = false,
  groupByUserSortOrder = null,
} = {}) => {
  switch (column.key) {
    case 'user':
      if (groupByUser) {
        column.sorter = (a, b) => compareGroupByUserColumn(a, b, groupByUserSortOrder);
        column.sortOrder = groupByUserSortOrder;
      } else {
        column.sorter = (a, b) => sortByObjProperty('user')(a, b);
      }
      column.showSorterTooltip = false;
      break;
    case 'date':
      column.sorter = (a, b) => sortByObjProperty('dateTimestamp')(a, b);
      column.showSorterTooltip = false;
      break;
    case 'project':
      column.sorter = (a, b) => compareProjectLabel(a, b, projectIdMap, settings);
      column.showSorterTooltip = false;
      break;
    default:
      break;
  }
  return column;
};
